/**
 * /books/:id — the API-backed data source for ChapterReview (spec §9).
 *
 * The component owns the round trips; ChapterReview stays pure. Confirming is three
 * calls in order — save the list, estimate it, confirm it — so the server's word on
 * validity and quota is what gates the spend, not the client's copy of the rules.
 */

import type { ApiFailure } from "../api/client";
import { FailedPage, LoadingPage, transient } from "../states/Page";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { Api, Book, ChapterPut, ChapterWire } from "../api/api";
import { ChapterReview, type Quota } from "./ChapterReview";
import type { SourceDocument } from "./types";
import { apiMessage } from "../text/errors";

export function toDocument(book: Book, chapters: ChapterWire[]): SourceDocument {
  const e = book.offset_evidence;
  return {
    filename: book.filename,
    pageCount: book.page_count,
    chapters: chapters.map((c) => ({
      number: c.number,
      title: c.title,
      startPage: c.start_page,
      endPage: c.end_page,
      confidence: c.detection_confidence,
      reason: c.detection_reason,
      included: c.included,
      ingested: c.ingested_at !== null,
      excerpt: c.excerpt ?? null,
    })),
    evidence: e.method
      ? {
          method: e.method,
          offset: e.offset ?? null,
          samples: (e.samples ?? []).map((s) => ({
            pdfPage: s.pdf_page,
            printed: s.printed,
            agrees: s.agrees,
          })),
          contentsPages: e.contents_pages ?? [],
          reason: e.reason ?? null,
          reasonCode: e.reason_code ?? null,
          scope: e.scope ?? null,
        }
      : null,
    gaps: (e.gaps ?? []).map((g) => ({
      startPage: g.start_page,
      endPage: g.end_page,
      label: g.label,
    })),
  };
}

export function toPayload(doc: SourceDocument): ChapterPut[] {
  return doc.chapters.map((c) => ({
    number: c.number,
    title: c.title,
    start_page: c.startPage,
    end_page: c.endPage,
    included: c.included ?? true,
  }));
}

function issuesFrom(details: unknown): string | null {
  if (typeof details !== "object" || !details || !("issues" in details)) return null;
  const issues = (details as { issues: { chapter: number | null; message: string }[] }).issues;
  return issues
    .map((i) => (i.chapter ? `Chapter ${i.chapter}: ${i.message}` : i.message))
    .join(" ");
}

const STATUS_TEXT: Record<Book["status"], string> = {
  detecting: "Still finding the chapters in this book — check back in a minute.",
  review: "",
  confirmed:
    "Chapters confirmed — your flashcards are being made. Come back to the subject to watch progress.",
  ingesting: "Making your flashcards from these chapters.",
  ready: "This book is ready to study — open it from the subject's documents.",
  failed: "",
};

export function BookScreen({
  api,
  bookId,
  onConfirmed,
  mode = "review",
  focusChapter,
  onCancel,
}: {
  api: Api;
  bookId: string;
  onConfirmed?: (book: Book) => void;
  /** "fix": change a ready book's chapters; only the changed ones are made again (spec 7). */
  mode?: "review" | "fix";
  focusChapter?: number;
  onCancel?: (book: Book) => void;
}) {
  const [book, setBook] = useState<Book | null>(null);
  const [chapters, setChapters] = useState<ChapterWire[] | null>(null);
  const [quota, setQuota] = useState<Quota | null>(null);
  const [meter, setMeter] = useState<{ used: number; limit: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failed, setFailed] = useState<ApiFailure | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    void Promise.all([api.getBook(bookId), api.listChapters(bookId), api.estimate(bookId)]).then(
      ([b, c, e]) => {
        setFailed(null);
        if (b.ok) setBook(b.data);
        else if (transient(b)) setFailed(b);
        else setError(apiMessage(b.error));
        setChapters(c.ok ? c.data.chapters : []);
        if (e.ok) setQuota(e.data.quota);
      },
    );
    void api.billingMe().then((r) => {
      const m = r.ok ? r.data.meters.find((x) => x.name === "pages_ingested") : undefined;
      if (m) setMeter({ used: m.used, limit: m.limit });
    });
  }, [api, bookId, attempt]);

  const documentId = book?.source_document_id ?? null;
  // Stable per document, so each thumbnail asks once.
  const preview = useCallback(
    async (page: number) => {
      if (!documentId) return null;
      const r = await api.getPreview(documentId, page);
      return r.ok ? r.data.url : null;
    },
    [api, documentId],
  );

  const doc = useMemo(
    () => (book && chapters ? toDocument(book, chapters) : null),
    [book, chapters],
  );

  if (failed && !book) {
    return <FailedPage failure={failed} onRetry={() => setAttempt((n) => n + 1)} />;
  }
  if (!book || !doc) return error ? <p role="alert">{error}</p> : <LoadingPage />;
  const fixing = mode === "fix";
  if (fixing && book.status !== "ready") {
    return (
      <main className="narrow">
        <h1>{book.title}</h1>
        <p>
          {book.status === "confirmed" || book.status === "ingesting"
            ? "Its flashcards are being made. You can fix its chapters when they’re ready."
            : "This book’s chapters can be fixed once its flashcards are made."}
        </p>
      </main>
    );
  }
  if (!fixing && book.status !== "review" && book.status !== "failed") {
    return (
      <main className="narrow">
        <h1>{book.title}</h1>
        <p>{STATUS_TEXT[book.status]}</p>
      </main>
    );
  }

  async function revise(edited: SourceDocument) {
    setBusy(true);
    setError(null);
    try {
      const r = await api.reviseChapters(bookId, toPayload(edited), false);
      if (!r.ok) {
        if (r.error === "quota_exceeded")
          return setError("That needs more pages than you have left this month.");
        return setError(issuesFrom(r.details) ?? apiMessage(r.error));
      }
      if (r.data.book) {
        setBook(r.data.book);
        onConfirmed?.(r.data.book);
      }
    } finally {
      setBusy(false);
    }
  }

  async function ingest(edited: SourceDocument) {
    setBusy(true);
    setError(null);
    try {
      const saved = await api.putChapters(bookId, toPayload(edited));
      if (!saved.ok) return setError(issuesFrom(saved.details) ?? apiMessage(saved.error));
      const est = await api.estimate(bookId);
      if (!est.ok) return setError(apiMessage(est.error));
      setQuota(est.data.quota);
      if (!est.data.quota.allowed) return;
      const confirmed = await api.confirm(bookId);
      if (!confirmed.ok) return setError(apiMessage(confirmed.error));
      setBook(confirmed.data);
      onConfirmed?.(confirmed.data);
    } finally {
      setBusy(false);
    }
  }

  return (
    <ChapterReview
      key={book.id}
      document={doc}
      quota={quota}
      meter={meter}
      busy={busy}
      error={error}
      onPreview={preview}
      onJump={async (page) => {
        const r = await api.getExcerpt(book.source_document_id, page);
        return r.ok ? r.data.excerpt : null;
      }}
      onIngest={(edited) => void (fixing ? revise(edited) : ingest(edited))}
      mode={mode}
      focusChapter={focusChapter}
      onCancel={fixing ? () => onCancel?.(book) : undefined}
      onPlan={
        fixing
          ? async (edited) => {
              const r = await api.reviseChapters(bookId, toPayload(edited), true);
              return r.ok ? r.data : null;
            }
          : undefined
      }
    />
  );
}
